import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import LotteryStatus from "./LotteryStatus";
import EligibilityStatus from "./EligibilityStatus";
import SubscriptionComplete from "./SubscriptionComplete";
import DrawResultWin from "./DrawResultWin";
import DrawResultLost from "./DrawResultLost";
import { LoadingSpinner, EventButton } from "../common";
import { useEventPolling } from "../../../hooks/newyear";
import {
  getLotteryStatus,
  subscribeLottery,
  checkEligibility,
} from "../../../store/api/newyearApi";
import { LOTTERY_USER_STATUS, POLLING_INTERVAL } from "../../../constants/newyearConstants";

const LotteryMain = ({ eventId }) => {
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  const [subscribedAt, setSubscribedAt] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);

  const {
    data: status,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useEventPolling(
    () => getLotteryStatus(eventId),
    {
      enabled: !!eventId && isAuthenticated,
      interval: POLLING_INTERVAL.STATUS_FAST,
      slowInterval: POLLING_INTERVAL.STATUS_FAST,
      pauseOnHidden: true,
    }
  );

  const userStatus = status?.userStatus;

  // 참가 자격 폴링
  const {
    data: eligibilityData,
    loading: eligibilityPollingLoading,
  } = useEventPolling(
    () => checkEligibility("LOTTERY"),
    {
      enabled: !!eventId && isAuthenticated,
      interval: POLLING_INTERVAL.STATUS_FAST,
      slowInterval: POLLING_INTERVAL.STATUS_FAST,
      pauseOnHidden: true,
      onSuccess: (result) => {
        setEligibility(result);
        setEligibilityLoading(false);
      },
      onError: (err) => {
        console.error("참가 자격 확인 실패:", err);
        setEligibility({ hasEligibility: false, message: "참가 자격 확인에 실패했습니다." });
        setEligibilityLoading(false);
      },
    }
  );

  // 초기 eligibility 로딩 상태 설정
  useEffect(() => {
    if (isAuthenticated && !eligibilityData && eligibilityPollingLoading) {
      setEligibilityLoading(true);
    }
  }, [isAuthenticated, eligibilityData, eligibilityPollingLoading]);

  const handleSubscribe = useCallback(async () => {
    if (!isAuthenticated || subscribing) return;
    // 참가 자격이 없으면 청약 불가
    if (!eligibility?.hasEligibility || !eligibility?.canParticipate) return;

    setSubscribing(true);
    setError(null);

    try {
      const result = await subscribeLottery(eventId);
      setSubscribedAt(result.subscribedAt);
      refetchStatus();
    } catch (err) {
      if (err.response?.data?.code === "ALREADY_SUBSCRIBED") {
        refetchStatus();
      } else {
        setError(err.response?.data?.message || "청약에 실패했습니다.");
      }
    } finally {
      setSubscribing(false);
    }
  }, [eventId, isAuthenticated, subscribing, eligibility, refetchStatus]);

  const handleGoHome = useCallback(() => {
    window.location.href = "/";
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="newyear-lottery">
        <div className="newyear-lottery__login-required">
          <p>이벤트 참여를 위해 로그인이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (statusLoading && !status) {
    return <LoadingSpinner message="이벤트 정보를 불러오는 중..." />;
  }

  if (error) {
    return (
      <div className="newyear-lottery__error">
        <p>{error}</p>
      </div>
    );
  }

  const renderContent = () => {
    // 참가 자격 로딩 중
    if (eligibilityLoading) {
      return <LoadingSpinner message="참가 자격을 확인하는 중..." />;
    }

    // 참가 자격이 없는 경우
    if (eligibility && !eligibility.hasEligibility) {
      return (
        <div className="newyear-lottery__no-eligibility">
          <div className="no-eligibility-icon">🎫</div>
          <h3>참가 자격이 없습니다</h3>
          <p>{eligibility.message}</p>
        </div>
      );
    }

    // 이미 참여한 경우 (청약 완료)
    if (eligibility?.alreadyParticipated) {
      return (
        <div className="newyear-lottery__already-participated">
          <div className="already-participated-icon">✅</div>
          <h3>이미 참여하셨습니다</h3>
          <p>{eligibility.message}</p>
        </div>
      );
    }

    if (userStatus === LOTTERY_USER_STATUS.WON) {
      return <DrawResultWin eventId={eventId} />;
    }

    if (userStatus === LOTTERY_USER_STATUS.LOST) {
      return <DrawResultLost onGoHome={handleGoHome} />;
    }

    if (userStatus === LOTTERY_USER_STATUS.SUBSCRIBED) {
      return (
        <SubscriptionComplete
          drawTime={status?.drawTime}
          subscribedAt={subscribedAt}
        />
      );
    }

    if (status?.eventStatus === "OPEN") {
      // 참가 자격이 없으면 청약 버튼 비활성화
      const canSubscribe = eligibility?.hasEligibility && eligibility?.canParticipate;

      return (
        <div className="newyear-lottery__subscribe">
          <p className="newyear-lottery__subscribe-message">
            청약 버튼을 눌러 추첨에 참여하세요!
          </p>
          <EventButton
            variant="primary"
            size="large"
            fullWidth
            onClick={handleSubscribe}
            loading={subscribing}
            disabled={!canSubscribe}
          >
            청약하기
          </EventButton>
        </div>
      );
    }

    if (status?.eventStatus === "SCHEDULED") {
      return (
        <div className="newyear-lottery__waiting">
          <p>청약이 아직 시작되지 않았습니다.</p>
        </div>
      );
    }

    if (status?.eventStatus === "CLOSED") {
      return (
        <div className="newyear-lottery__closed">
          <p>청약이 마감되었습니다.</p>
          <p>추첨 결과를 기다려주세요.</p>
        </div>
      );
    }

    if (status?.eventStatus === "ENDED") {
      return (
        <div className="newyear-lottery__ended">
          <p>이벤트가 종료되었습니다.</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="newyear-lottery">
      <LotteryStatus status={status} userStatus={userStatus} />
      {eligibility && (
        <EligibilityStatus eligibility={eligibility} />
      )}
      <div className="newyear-lottery__content">{renderContent()}</div>
    </div>
  );
};

export default LotteryMain;
